<template>
    <o-modal @close="closeForm">
        <template v-slot:header>
            <h5 class="modal-title">{{ isEdit ? 'Edit Campaign' : 'Create Campaign' }}</h5>
        </template>
        <template v-slot:body>
            <div class="form-group mb-3">
                <label for="campaign_name" class="form-label">Campaign Name <span class="text-danger">*</span></label>
                <input
                    type="text"
                    class="form-control"
                    id="campaign_name"
                    v-model="formData.name"
                    :class="{ 'is-invalid': errors.name }"
                    placeholder="Enter campaign name"
                />
                <div v-if="errors.name" class="invalid-feedback">{{ errors.name }}</div>
            </div>

            <div class="row mb-3">
                <div class="col-md-6">
                    <label for="business_type" class="form-label">Business Type <span class="text-danger">*</span></label>
                    <select
                        class="form-select"
                        id="business_type"
                        v-model="formData.businessable_type"
                        :class="{ 'is-invalid': errors.businessable_type }"
                        @change="onBusinessTypeChange"
                        :disabled="isEdit"
                    >
                        <option value="">Select business type</option>
                        <option value="Corporate">Corporate</option>
                        <option value="Branch">Branch</option>
                        <option value="Merchant">Merchant</option>
                    </select>
                    <div v-if="errors.businessable_type" class="invalid-feedback">{{ errors.businessable_type }}</div>
                </div>
                
                <div class="col-md-6">
                    <label for="business_select" class="form-label">Select Business <span class="text-danger">*</span></label>
                    <select
                        class="form-select"
                        id="business_select"
                        v-model="formData.businessable_id"
                        :class="{ 'is-invalid': errors.businessable_id }"
                        :disabled="!formData.businessable_type || isLoadingBusiness || isEdit"
                    >
                        <option value="">{{ getBusinessPlaceholder() }}</option>
                        <option v-for="item in businessList" :key="item.id" :value="item.id">
                            {{ item.name }}
                        </option>
                    </select>
                    <div v-if="errors.businessable_id" class="invalid-feedback">{{ errors.businessable_id }}</div>
                    <div v-if="isLoadingBusiness" class="form-text">Loading...</div>
                </div>
            </div>
            
            <div class="row mb-3">
                <div class="col-md-6">
                    <label for="validity_start" class="form-label">Validity Start Date <span class="text-danger">*</span></label>
                    <input
                        type="date"
                        class="form-control"
                        id="validity_start"
                        v-model="formData.validity_start"
                        :class="{ 'is-invalid': errors.validity_start }"
                        />
                        <div v-if="errors.validity_start" class="invalid-feedback">{{ errors.validity_start }}</div>
                    </div>
                    
                    <div class="col-md-6">
                        <label for="validity_end" class="form-label">Validity End Date <span class="text-danger">*</span></label>
                    <input
                        type="date"
                        class="form-control"
                        id="validity_end"
                        v-model="formData.validity_end"
                        :class="{ 'is-invalid': errors.validity_end }"
                        :min="formData.validity_start"
                    />
                    <div v-if="errors.validity_end" class="invalid-feedback">{{ errors.validity_end }}</div>
                </div>
            </div>
            
            <div class="form-group mb-3">
                <label for="target_link" class="form-label">Target Link <span class="text-danger">*</span></label>
                <input
                    type="url"
                    class="form-control"
                    id="target_link"
                    v-model="formData.target_link"
                    :class="{ 'is-invalid': errors.target_link }"
                    placeholder="https://example.com"
                />
                <div v-if="errors.target_link" class="invalid-feedback">{{ errors.target_link }}</div>
            </div>

            <div class="form-group mb-3">
                <label for="code" class="form-label">Referral Code</label>
                <input
                    type="text"
                    class="form-control"
                    id="code"
                    v-model="formData.code"
                    placeholder="Leave empty to auto-generate"
                    :class="{ 'is-invalid': errors.code }"
                    :disabled="isEdit"
                />
                <div v-if="errors.code" class="invalid-feedback">{{ errors.code }}</div>
                <div class="form-text"><i class="fas fa-info-circle me-1"></i> Leave empty to auto-generate unique code</div>
            </div>
            
            <div class="d-flex justify-content-end gap-2 mt-4">
                <o-button variant="secondary" @click="closeForm">Cancel</o-button>
                <o-button variant="primary" @click="submit" :disabled="isSubmitting">
                    {{ isSubmitting ? 'Saving...' : 'Save' }}
                </o-button>
            </div>
        </template>
    </o-modal>
</template>

<script>
import { ref, reactive, computed, onMounted } from 'vue'
import { useStore } from 'vuex'
import { OModal, OButton } from '@/components/OmnihControl'

export default {
    name: 'CampaignFormNew',
    components: {
        OModal,
        OButton
    },
    props: {
        editedItem: {
            type: Object,
            default: null
        },
        marketingId: {
            type: String,
            required: true
        }
    },
    emits: ['save', 'close'],
    setup(props, { emit }) {
        const store = useStore()

        const isEdit = computed(() => !!props.editedItem)
        const isSubmitting = ref(false)
        const isLoadingBusiness = ref(false)

        const formData = reactive({
            id: null,
            name: '',
            marketing_id: props.marketingId,
            businessable_type: '',
            businessable_id: '',
            code: '',
            target_link: '',
            validity_start: '',
            validity_end: ''
        })

        const errors = reactive({
            name: '',
            businessable_type: '',
            businessable_id: '',
            target_link: '',
            validity_start: '',
            validity_end: '',
            code: ''
        })

        const businessList = ref([])
        const businessProfiles = ref(null)

        onMounted(async () => {
            // Load business profiles dari Auth
            await loadBusinessProfiles()

            if (props.editedItem) {
                formData.id = props.editedItem.id
                formData.name = props.editedItem.name || ''
                formData.businessable_type = getBusinessTypeShort(props.editedItem.businessable_type)
                formData.businessable_id = props.editedItem.businessable_id || ''
                formData.code = props.editedItem.code || ''
                formData.target_link = props.editedItem.target_link || ''
                formData.validity_start = formatDateForInput(props.editedItem.validity_start)
                formData.validity_end = formatDateForInput(props.editedItem.validity_end)

                if (formData.businessable_type) {
                    loadBusinessList(formData.businessable_type)
                }
            }
        })

        async function loadBusinessProfiles() {
            try {
                await store.dispatch('Auth/read')
                businessProfiles.value = store.state.Auth.businessProfiles
            } catch (err) {
                errors.businessable_type = err.message || 'Failed to load business profiles'
            }
        }

        function getBusinessTypeShort(fullType) {
            if (!fullType) return ''
            return fullType.replace('App\\Models\\', '')
        }

        function formatDateForInput(date) {
            if (!date) return ''
            const d = new Date(date)
            const year = d.getFullYear()
            const month = String(d.getMonth() + 1).padStart(2, '0')
            const day = String(d.getDate()).padStart(2, '0')
            return `${year}-${month}-${day}`
        }

        function onBusinessTypeChange() {
            formData.businessable_id = ''
            businessList.value = []
            
            if (formData.businessable_type) {
                loadBusinessList(formData.businessable_type)
            }
        }

        function loadBusinessList(type) {
            isLoadingBusiness.value = true
            businessList.value = []
            
            try {
                if (!businessProfiles.value) {
                    return
                }

                switch(type) {
                    case 'Corporate': {
                        businessList.value = businessProfiles.value.corporate?.map(v => ({
                            id: v.id,
                            name: v.name
                        })) || []
                        break
                    }
                    
                    case 'Branch': {
                        const allBranches = []
                        businessProfiles.value.corporate?.forEach(corporate => {
                            corporate.branch?.forEach(branch => {
                                allBranches.push({
                                    id: branch.id,
                                    name: `${branch.name} (${corporate.name})`
                                })
                            })
                        })
                        businessList.value = allBranches
                        break
                    }
                    
                    case 'Merchant': {
                        const allMerchants = []
                        businessProfiles.value.corporate?.forEach(corporate => {
                            corporate.branch?.forEach(branch => {
                                branch.merchant?.forEach(merchant => {
                                    allMerchants.push({
                                        id: merchant.id,
                                        name: `${merchant.name} (${branch.name})`
                                    })
                                })
                            })
                        })
                        businessList.value = allMerchants
                        break
                    }
                }
            } catch (err) {
                errors.businessable_type = err.message || 'Failed to load business list'
            } finally {
                isLoadingBusiness.value = false
            }
        }

        function getBusinessPlaceholder() {
            if (!formData.businessable_type) return 'Select business type first'
            if (isLoadingBusiness.value) return 'Loading...'
            return `Select ${formData.businessable_type.toLowerCase()}`
        }

        function validate() {
            // Reset errors
            Object.keys(errors).forEach(key => errors[key] = '')

            let isValid = true

            if (!formData.name || formData.name.trim() === '') {
                errors.name = 'Campaign name is required'
                isValid = false
            } else if (formData.name.trim().length > 255) {
                errors.name = 'Campaign name must not exceed 255 characters'
                isValid = false
            }

            if (!formData.businessable_type) {
                errors.businessable_type = 'Business type is required'
                isValid = false
            }

            if (!formData.businessable_id) {
                errors.businessable_id = 'Business selection is required'
                isValid = false
            }

            if (!formData.target_link || formData.target_link.trim() === '') {
                errors.target_link = 'Target link is required'
                isValid = false
            }

            if (!formData.validity_start) {
                errors.validity_start = 'Validity start date is required'
                isValid = false
            }

            if (!formData.validity_end) {
                errors.validity_end = 'Validity end date is required'
                isValid = false
            }

            if (formData.target_link && !isValidUrl(formData.target_link)) {
                errors.target_link = 'Please enter a valid URL'
                isValid = false
            }

            if (formData.validity_start && formData.validity_end) {
                if (new Date(formData.validity_end) < new Date(formData.validity_start)) {
                    errors.validity_end = 'End date must be after start date'
                    isValid = false
                }
            }

            return isValid
        }

        function isValidUrl(string) {
            try {
                new URL(string)
                return true
            } catch (_) {
                return false
            }
        }

        async function submit() {
            if (!validate()) return

            isSubmitting.value = true

            const dataToSubmit = {
                ...formData,
                name: formData.name.trim(),
                target_link: formData.target_link.trim(),
                code: formData.code.trim() || undefined
            }

            try {
                // Emit save event and wait for parent to complete
                await emit('save', dataToSubmit)
            } catch (error) {
                console.error('Save failed:', error)
            } finally {
                isSubmitting.value = false
            }
        }

        function closeForm() {
            emit('close')
        }

        return {
            isEdit,
            isSubmitting,
            isLoadingBusiness,
            formData,
            errors,
            businessList,
            onBusinessTypeChange,
            getBusinessPlaceholder,
            submit,
            closeForm
        }
    }
}
</script>

<style scoped>
.form-label {
    font-weight: 500;
    color: #333;
}

.text-danger {
    color: #dc3545;
}

.is-invalid {
    border-color: #dc3545;
}

.invalid-feedback {
    display: block;
    color: #dc3545;
    font-size: 0.875rem;
    margin-top: 0.25rem;
}

.form-text {
    font-size: 0.875rem;
    color: #6c757d;
}

.form-control:focus,
.form-select:focus {
    border-color: var(--primary-color, #F93E3E) !important;
    box-shadow: 0 0 0 0.25rem var(--secondary-color, #FEE9E9) !important;
}
</style>
